import { useMemo } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ShieldCheck, 
  AlertTriangle, 
  UserCheck, 
  FileText, 
  Lock,
  CheckCircle2,
  Flag,
  Link2
} from 'lucide-react';

interface CaseEntry {
  id: string;
  patientName: string;
  patientId: string;
  dateOfService: string;
  surgeon: string;
  payer: string;
  tripleMatch: {
    authorization: 'match' | 'gap' | 'pending';
    documentation: 'match' | 'gap' | 'pending';
    surgeonConfirmation: 'match' | 'gap' | 'pending';
  };
  procedure: string;
  cptCode: string;
}

interface CaseIntegrityPanelProps {
  caseData: CaseEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CaseIntegrityPanel({ caseData, open, onOpenChange }: CaseIntegrityPanelProps) {
  const auditHash = useMemo(() => {
    const chars = '0123456789abcdef';
    return Array.from({ length: 16 }, () => chars[Math.floor(Math.random() * 16)]).join('');
  }, []);

  if (!caseData) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-card border-border">
        <SheetHeader className="space-y-3 pb-4 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <SheetTitle className="text-base font-semibold text-foreground leading-tight">
              Case Integrity Review: {caseData.patientName}
            </SheetTitle>
            <Badge className="bg-success/15 text-success border-success/30 shrink-0 text-[11px]">
              <ShieldCheck className="w-3 h-3 mr-1" />
              Audit-Ready
            </Badge>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="font-mono">{caseData.id}</span>
            <span>{caseData.patientId}</span>
            <span>{caseData.surgeon}</span>
            <span>{caseData.payer}</span>
          </div>
        </SheetHeader>

        <div className="space-y-5 pt-5">
          {/* Section Title */}
          <div>
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              Integrity Check Results
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Triple-match verification: Authorization × Documentation × Surgeon Confirmation
            </p>
          </div>

          {/* Box 1: Authorized CPTs */}
          <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <CheckCircle2 className="w-3.5 h-3.5 text-success" />
              Authorized CPTs
            </div>
            <div className="flex items-center gap-2 pl-1">
              <span className="font-mono text-sm text-foreground font-medium">{caseData.cptCode}</span>
              <span className="text-xs text-muted-foreground">— {caseData.procedure}</span>
            </div>
            <div className="text-[11px] text-success flex items-center gap-1.5 mt-1">
              <CheckCircle2 className="w-3 h-3" />
              Pre-op authorization verified
            </div>
          </div>

          {/* Box 2: Documented Evidence */}
          <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <FileText className="w-3.5 h-3.5 text-primary" />
              Documented Evidence
            </div>
            <div className="bg-background/60 rounded-md p-3 border border-border/50">
              <p className="text-sm text-foreground/90 leading-relaxed italic">
                "…medial parapatellar approach was utilized. I then performed a{' '}
                <span className="bg-warning/20 text-warning font-semibold px-1 rounded not-italic">
                  patellar resurfacing
                </span>{' '}
                with a 32mm onlay component. Trial reduction showed good tracking…"
              </p>
            </div>

            {/* Discrepancy Warning */}
            <div className="flex items-start gap-2.5 rounded-md bg-warning/10 border border-warning/25 p-3">
              <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-xs font-medium text-warning">
                  Discrepancy Detected
                </p>
                <p className="text-[11px] text-foreground/80 leading-relaxed">
                  CPT 27438 (Patellar Resurfacing) found in operative documentation but{' '}
                  <span className="font-semibold text-warning">missing from pre-op authorization</span>.
                </p>
              </div>
            </div>
          </div>

          {/* Surgeon Confirmation Status */}
          <div className="rounded-lg border border-border bg-secondary/30 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <UserCheck className="w-3.5 h-3.5 text-muted-foreground" />
              Surgeon Confirmation
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {caseData.tripleMatch.surgeonConfirmation === 'match'
                ? '✓ Surgeon has confirmed all performed procedures.'
                : 'Awaiting surgeon attestation and procedure confirmation.'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-2 pt-1">
            <Button className="w-full bg-success hover:bg-success/90 text-success-foreground gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Confirm Matches
            </Button>
            <Button variant="outline" className="w-full gap-2 border-warning/40 text-warning hover:bg-warning/10 hover:text-warning">
              <Flag className="w-4 h-4" />
              Flag for Surgeon Review
            </Button>
            <Button variant="outline" className="w-full gap-2">
              <FileText className="w-4 h-4" />
              Generate Immutable Audit Packet
            </Button>
          </div>

          {/* Integrity Logs — Blockchain-style ledger */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Link2 className="w-4 h-4 text-primary" />
              Integrity Logs
            </h3>
            <div className="space-y-0 rounded-lg border border-border overflow-hidden">
              {[
                { time: '08:14:22 AM', event: 'Authorization Verified by Payer Portal.' },
                { time: '11:47:03 AM', event: 'Intra-Op Dictation Received.' },
                { time: '11:48:16 AM', event: 'Triple-Match Engine identified CPT mismatch.' },
              ].map((log, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 px-3 py-2.5 border-b border-border/50 last:border-b-0 bg-secondary/20 hover:bg-secondary/40 transition-colors"
                >
                  <span className="font-mono text-[11px] text-primary shrink-0 pt-px">{log.time}</span>
                  <span className="text-xs text-foreground/80">{log.event}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Zero-Guess Policy Footer */}
          <div className="rounded-lg border border-border bg-muted/30 p-3 mt-2">
            <div className="flex items-start gap-2">
              <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                <span className="font-semibold text-foreground/70">Zero-Guess Policy:</span>{' '}
                LINK has NOT added this code to the claim. Surgeon must manually validate or request authorization update. All actions are logged in the immutable audit trail.
              </p>
            </div>
          </div>

          {/* Security Footer */}
          <div className="border-t border-border pt-3 mt-3">
            <p className="text-[10px] font-mono text-muted-foreground/60 text-center">
              Immutable Audit Trail ID: 0x{auditHash} | HIPAA Hardened Environment
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
